/**
 * Field-app view of the existing discipline configs.
 *
 * Superintendents see shop + field progress steps. UDFs, imports, and
 * analytics stay on the website.
 */

import { getDisciplineConfig } from '../../config/disciplines';

export const mobileAppBase = (pathname = '') => (
  pathname.startsWith('/m/preview') ? '/m/preview' : '/m'
);

export const FIELD_APP_DISCIPLINE_IDS = [
  'piping',
  'mechanical',
  'structural',
  'hvac',
  'ducts',
  'boilermaker',
  'electrical',
  'civil',
  'vessels',
  'chutes',
  'conveyors',
  'skids',
];

const STEP_GROUPS = new Set(['Field', 'Field Progress', 'Receipt', 'Shop']);

export const PHOTO_LINK_TYPE = {
  piping: 'TRACKER_PIPING',
  mechanical: 'TRACKER_MECHANICAL',
  structural: 'TRACKER_STRUCTURAL',
  hvac: 'OTHER',
  ducts: 'OTHER',
  boilermaker: 'OTHER',
  electrical: 'OTHER',
  civil: 'OTHER',
  vessels: 'OTHER',
  chutes: 'OTHER',
  conveyors: 'OTHER',
  skids: 'OTHER',
};

const SHORT_NAMES = {
  piping: 'Piping',
  mechanical: 'Mechanical',
  structural: 'Structural',
  hvac: 'HVAC',
  ducts: 'Ducts',
  boilermaker: 'Boilermaker',
  electrical: 'Electrical',
  civil: 'Civil',
  vessels: 'Vessels',
  chutes: 'Chutes',
  conveyors: 'Conveyors',
  skids: 'Skids',
};

const ACCENT = {
  piping: '#c62828',
  mechanical: '#1565c0',
  structural: '#2e7d32',
  hvac: '#6a1b9a',
  ducts: '#ad1457',
  boilermaker: '#ef6c00',
  electrical: '#f9a825',
  civil: '#37474f',
  vessels: '#00838f',
  chutes: '#5d4037',
  conveyors: '#455a64',
  skids: '#283593',
};

export const cleanStepLabel = (header) =>
  String(header || '')
    .replace(/\s*%\s*$/, '')
    .replace(/\s+/g, ' ')
    .trim();

const stepGroup = (headerGroup) => (
  headerGroup === 'Shop' ? 'shop' : 'field'
);

export const getMobileDiscipline = (disciplineId) => {
  const config = getDisciplineConfig(disciplineId);
  const steps = (config.columns || [])
    .filter((column) => (
      column.type === 'progress'
      && column.editable
      && STEP_GROUPS.has(column.headerGroup)
      && column.showInDashboard !== false
    ))
    .map((column) => ({
      id: column.id,
      label: cleanStepLabel(column.header),
      group: stepGroup(column.headerGroup),
      options: Array.isArray(column.options) && column.options.length
        ? column.options
        : [0, 25, 50, 75, 100],
      dbField: column.dbField,
    }));

  const isPiping = config.id === 'piping';

  return {
    id: config.id,
    menuId: config.menuId,
    name: SHORT_NAMES[config.id] || config.name,
    fullName: config.name,
    accent: ACCENT[config.id] || '#c62828',
    steps,
    shopSteps: steps.filter((step) => step.group === 'shop'),
    fieldSteps: steps.filter((step) => step.group === 'field'),
    titleFields: isPiping ? ['isoSheetNumber'] : ['equipmentTagOrCode'],
    subtitleFields: isPiping
      ? ['fluidCode', 'size1', 'material1']
      : ['description'],
    metaFields: isPiping
      ? ['priorityName', 'testPackage1']
      : ['area', 'subArea', 'buildingNumber'],
    searchFields: isPiping
      ? ['isoSheetNumber', 'fluidCode', 'size1', 'material1', 'pAndId', 'testPackage1', 'comments']
      : ['equipmentTagOrCode', 'description', 'area', 'subArea', 'buildingNumber', 'drawingNumber', 'pAndId', 'comments'],
    filterField: isPiping ? 'fluidCode' : 'area',
    filterLabel: isPiping ? 'Fluid' : 'Area',
    onHoldField: isPiping ? 'onHold' : null,
    config,
  };
};

export const getFieldAppDisciplines = () =>
  FIELD_APP_DISCIPLINE_IDS.map((id) => getMobileDiscipline(id));

export const lineTitle = (discipline, row) => {
  for (const field of discipline.titleFields) {
    const value = row?.[field];
    if (value) return String(value);
  }
  return `Item ${row?.id ?? ''}`.trim();
};

export const lineSubtitle = (discipline, row) =>
  discipline.subtitleFields
    .map((field) => row?.[field])
    .filter(Boolean)
    .join(' · ');

export const lineMeta = (discipline, row) =>
  discipline.metaFields
    .map((field) => row?.[field])
    .filter(Boolean)
    .join(' · ');

export const lineSearchText = (discipline, row) =>
  discipline.searchFields
    .map((field) => row?.[field])
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

export const photoTagForLine = (discipline, row) => {
  const title = lineTitle(discipline, row);
  return {
    linked_item_type: PHOTO_LINK_TYPE[discipline.id] || 'OTHER',
    linked_item_id: title,
    description: `${discipline.name} · ${title}`,
    category: 'Progress',
    title,
  };
};

export const photoMatchesLine = (photo, discipline, row) => {
  const tag = photoTagForLine(discipline, row);
  const tags = photo?.tags || [];
  if (tags.some((item) => (
    String(item.linked_item_id || '') === String(tag.linked_item_id)
    || String(item.description || '').includes(tag.title)
  ))) return true;
  const hay = `${photo?.title || ''} ${photo?.description || ''}`.toLowerCase();
  return hay.includes(String(tag.title).toLowerCase());
};

export const stepValue = (row, stepId) => {
  const value = Number(row?.[stepId]);
  return Number.isFinite(value) ? value : 0;
};

export const lineOverallPercent = (row, steps) => {
  if (!steps.length) return 0;
  const total = steps.reduce((sum, step) => sum + stepValue(row, step.id), 0);
  return Math.round(total / steps.length);
};

export const lineNeedsWork = (row, steps) =>
  steps.some((step) => stepValue(row, step.id) < 100);

export const nextIncompleteStep = (row, steps) =>
  steps.find((step) => stepValue(row, step.id) < 100) || null;

export const uniqueFilterValues = (rows, field) => {
  if (!field) return [];
  const seen = new Set();
  rows.forEach((row) => {
    const value = String(row?.[field] || '').trim();
    if (value) seen.add(value);
  });
  return Array.from(seen).sort((a, b) => a.localeCompare(b));
};

export const stepsForScope = (discipline, scope = 'all') => {
  if (scope === 'shop') return discipline.shopSteps || [];
  if (scope === 'field') return discipline.fieldSteps || [];
  return discipline.steps || [];
};

export const lineSplitPercent = (row, discipline) => ({
  shop: discipline.shopSteps?.length ? lineOverallPercent(row, discipline.shopSteps) : null,
  field: discipline.fieldSteps?.length ? lineOverallPercent(row, discipline.fieldSteps) : null,
  overall: lineOverallPercent(row, discipline.steps),
});

export const groupLines = (rows, discipline) => {
  const field = discipline?.filterField;
  const groups = new Map();
  rows.forEach((row) => {
    const key = String(row?.[field] || '').trim() || `No ${discipline.filterLabel || 'group'}`;
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(row);
  });
  return Array.from(groups.entries()).sort((a, b) => a[0].localeCompare(b[0]));
};

export const filterLines = (rows, discipline, {
  query = '',
  filterValue = '',
  needsWork = false,
  scope = 'all',
  onHoldOnly = false,
} = {}) => {
  const needle = query.trim().toLowerCase();
  const steps = stepsForScope(discipline, scope);
  const walkSteps = steps.length ? steps : discipline.steps;
  return rows.filter((row) => {
    if (row?.isSuperseded) return false;
    if (onHoldOnly && !row?.onHold) return false;
    if (needsWork && !lineNeedsWork(row, walkSteps)) return false;
    if (filterValue && String(row?.[discipline.filterField] || '') !== filterValue) return false;
    if (needle && !lineSearchText(discipline, row).includes(needle)) return false;
    return true;
  });
};

export const neighborsInList = (walkRows, allRows, rowId) => {
  const current = String(rowId);
  const walkIds = walkRows.map((row) => String(row.id));
  const idx = walkIds.indexOf(current);
  if (idx >= 0) {
    return {
      prev: idx > 0 ? walkRows[idx - 1].id : null,
      next: idx < walkRows.length - 1 ? walkRows[idx + 1].id : null,
    };
  }
  const allIds = allRows.map((row) => String(row.id));
  const allIdx = allIds.indexOf(current);
  if (allIdx < 0) return { prev: null, next: null };
  const next = walkRows.find((row) => allIds.indexOf(String(row.id)) > allIdx);
  const prev = [...walkRows].reverse().find((row) => allIds.indexOf(String(row.id)) < allIdx);
  return { prev: prev?.id ?? null, next: next?.id ?? null };
};

export const optGridClass = (step) => {
  const count = step?.options?.length || 0;
  if (count === 2) return 'lgl-m-opts-2';
  if (count === 5) return 'lgl-m-opts-5';
  return 'lgl-m-opts-3';
};

export const normalizeMatrixPayload = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.rows)) return data.rows;
  if (Array.isArray(data?.data)) return data.data;
  return [];
};

export const applySavedTotals = (row, savedRow) => {
  if (!savedRow) return row;
  const next = { ...row };
  const newTotal = savedRow.total_field_completion ?? savedRow.field_total_completion ?? savedRow.totalFieldCompletion;
  if (newTotal !== undefined && newTotal !== null) {
    next.totalFieldCompletion = Math.round(Number(newTotal));
  }
  return next;
};
